import React, {useMemo} from 'react';
import {Heading, Text, Button, Center} from '@tempo/core';
import {NavLink} from '@tempo/nextjs';
import {useTranslation} from 'next-i18next';
import {useActiveArtist, useUser} from '@deezer/next/contexts/main/hooks';
import {getUserDefaultRoute} from '@deezer/next/shared/utils/user';
import HttpStatusCode from '@deezer/next/services/types/HttpStatusCode';
import {typographyOverrides} from '@deezer/next/shared/utils/typographyOverrides';

type ErrorPageProps = {
	type?: HttpStatusCode | 'client';
	content?: React.ReactNode;
	action?: React.ReactNode;
	showDefaultAction?: boolean;
};

const ErrorPage = ({
	type = HttpStatusCode.NOT_FOUND,
	content,
	action,
	showDefaultAction = true,
}: ErrorPageProps) => {
	const user = useUser();
	const activeArtist = useActiveArtist();
	const {t} = useTranslation('d4c');

	const {message, subtitle} = useMemo(() => {
		switch (type) {
			case 'client':
				return {
					message: t('errormessage_text_anunexpectederroroccurred_d4c-web'),
					subtitle: t('errormessage_text_tryagainorcontactus_d4c-web'),
				};
			case HttpStatusCode.BAD_REQUEST:
				return {
					message: t('errormessage_title_somethingwentwrong_d4c-web'),
					subtitle: t('errormessage_text_ensurerequestcorrect_d4c-web'),
				};
			case HttpStatusCode.FORBIDDEN:
				return {
					message: t('errormessage_title_pagehasrestrictedaccess_d4c-web'),
					subtitle: t('errormessage_text_ucantaccesspagedirectly_d4c-web'),
				};
			case HttpStatusCode.NOT_FOUND:
				return {
					message: t('errormessage_title_pagenotfound_d4c-web'),
					subtitle: t('errormessage_text_wecantfindpage_d4c-web'),
				};
			case HttpStatusCode.INTERNAL_SERVER_ERROR:
			default:
				return {
					message: t('errormessage_title_internalerroroccurred_d4c-web'),
					subtitle: t('errormessage_text_wecantfindpage_d4c-web'),
				};
		}
	}, [type, t]);

	const defaultAction = (
		<NavLink href={getUserDefaultRoute(user, activeArtist)}>
			<Button data-testid="back-dashboard" mt={8}>
				{t('generic_action_returntodashboard_d4c-web')}
			</Button>
		</NavLink>
	);

	return (
		<Center
			height="80vh"
			sx={{
				textAlign: 'center',
				position: 'absolute',
				top: 0,
				bottom: 0,
				left: 0,
				right: 0,
			}}
		>
			<section className="container">
				<Heading as="h1" sx={typographyOverrides['display.l']} mb={8}>
					{type === 'client'
						? t('errormessage_title_somethingwentwrong_d4c-web')
						: type}
				</Heading>

				{content ?? (
					<>
						<Heading as="h2" sx={typographyOverrides['display.s']} mb={4}>
							{message}
						</Heading>

						<Text
							variant="body.default.regular"
							mb={2}
							color="text.neutral.secondary.default"
						>
							{subtitle}
						</Text>
					</>
				)}

				{action ?? (showDefaultAction ? defaultAction : null)}
			</section>
		</Center>
	);
};

export default ErrorPage;
